import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Save,
    HardDrive,
    Wallet,
    Palette,
    Type,
    Image as ImageIcon,
    Calendar,
    TrendingUp,
    Award,
    RotateCcw,
    HelpCircle,
    Mail,
    Send,
    Eye,
    EyeOff,
    Shield,
    Trash2,
    AlertTriangle,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { usePlatformConfig, FONT_OPTIONS } from "../../lib/platformConfig";
import { fmtCop, fmtBytes, Stat, CreditStat, ColorField, fmtCopDigits, parseCopDigits, useCurrencyPref } from "./_shared";
import { useConfirm } from "../../components/ConfirmDialog";

/**
 * Tiny inline help icon — hover/focus shows a tooltip explaining what the
 * adjacent label represents. Uses `title` for accessibility and a custom
 * tailwind-only popover for visual polish (peer-hover/peer-focus).
 *
 *   <HelpHint text="Promedio real ponderado…" />
 */
function HelpHint({ text }) {
    return (
        <span className="relative inline-flex items-center group ml-1.5 align-middle">
            <button
                type="button"
                tabIndex={0}
                aria-label={text}
                title={text}
                className="text-fog-400 hover:text-periwinkle-500 focus:text-periwinkle-500 outline-none transition-colors peer"
            >
                <HelpCircle className="w-3.5 h-3.5" />
            </button>
            <span className="pointer-events-none absolute left-1/2 top-full mt-1.5 z-30 w-60 -translate-x-1/2 rounded-lg border hairline border-ink-600 bg-ink-900 px-3 py-2 text-[11px] font-mono text-fog-200 normal-case tracking-normal shadow-lg opacity-0 peer-hover:opacity-100 peer-focus:opacity-100 transition-opacity">
                {text}
            </span>
        </span>
    );
}

/**
 * Build a list of the last `count` months (inclusive of current).
 * Returns [{value: "2026-02", label: "Febrero 2026"}, ...] newest first.
 */
function buildMonthOptions(count = 12) {
    const now = new Date();
    const out = [];
    for (let i = 0; i < count; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const value = `${yyyy}-${mm}`;
        const label = d.toLocaleDateString("es-CO", {
            month: "long",
            year: "numeric",
        });
        out.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return out;
}

const fmtUsd = (v) =>
    `$${(v || 0).toLocaleString("es-CO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} USD`;

/**
 * Format a COP amount as either COP or USD based on currency.
 * When currency='USD', divides by `trm`.
 */
function fmtCurrency(copValue, currency, trm) {
    if (currency === "USD") {
        const usd = trm > 0 ? (copValue || 0) / trm : 0;
        return fmtUsd(usd);
    }
    return fmtCop(copValue);
}

/**
 * Admin sub-page: general platform configuration.
 *
 * Everything that doesn't belong to a specific model lives here:
 *   - COP monthly budget (user quota + super-admin cost/image)
 *   - Platform storage limit + per-brand breakdown
 *   - Headline activity counters
 *   - White-label config (colors, font, favicon, logo, platform name)
 */
export default function AdminGeneral() {
    const { user: me } = useAuth();
    const isSuper = me?.role === "super_admin";

    const [stats, setStats] = useState(null);
    const [credits, setCredits] = useState(null);
    const [storage, setStorage] = useState(null);
    const [storageBreakdown, setStorageBreakdown] = useState([]);
    const [storageInput, setStorageInput] = useState("");
    const [savingStorage, setSavingStorage] = useState(false);
    const [consumption, setConsumption] = useState(null);
    const [budgetInput, setBudgetInput] = useState("");
    // SMTP — super admin only. `smtpForm.password = ""` means "keep current"
    // when the backend already has one stored (`smtp.has_password === true`).
    const [smtp, setSmtp] = useState(null);
    const [smtpForm, setSmtpForm] = useState({
        enabled: false,
        server: "",
        port: 587,
        user: "",
        password: "",
        from_email: "",
        from_name: "",
        use_tls: true,
    });
    const [showSmtpPwd, setShowSmtpPwd] = useState(false);
    const [savingSmtp, setSavingSmtp] = useState(false);
    const [testingSmtp, setTestingSmtp] = useState(false);
    const [smtpTestEmail, setSmtpTestEmail] = useState("");
    // reCAPTCHA — super admin only. Mirrors the SMTP pattern: empty
    // `secret_key` on save means "keep the stored value".
    const [recaptcha, setRecaptcha] = useState(null);
    const [recaptchaForm, setRecaptchaForm] = useState({
        enabled: false,
        site_key: "",
        secret_key: "",
    });
    const [showRecaptchaSecret, setShowRecaptchaSecret] = useState(false);
    const [savingRecaptcha, setSavingRecaptcha] = useState(false);
    const [resettingLibrary, setResettingLibrary] = useState(false);
    const [totalBudgetUsdInput, setTotalBudgetUsdInput] = useState("");
    const [savingCop, setSavingCop] = useState(false);
    // NEW — month filter + currency + TRM + elite plan fee
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });
    // Currency is per-user (localStorage) so any admin can pick USD or COP
    // without changing anyone else's view. Hydrates from backend default
    // until the user picks a local preference.
    const [backendCurrency, setBackendCurrency] = useState("USD");
    const [currency, setCurrency] = useCurrencyPref(backendCurrency);
    const [trmInput, setTrmInput] = useState("");
    const [elitePlanInput, setElitePlanInput] = useState("");
    const [savingPrefs, setSavingPrefs] = useState(false);
    // Chat defaults + per-quality surcharges. All four flow through the
    // `/credits/cop-config` endpoint (super_admin only). Resolutions list
    // comes from the same `/catalog/resolutions` the chat already uses.
    const [resolutionsCatalog, setResolutionsCatalog] = useState([]);
    const [defaultQuality, setDefaultQuality] = useState("basic");
    const [defaultResolutionId, setDefaultResolutionId] = useState("square_hd");
    const [qualitySurcharge, setQualitySurcharge] = useState({
        basic: "0",
        medium: "0.5",
        high: "0.7",
    });
    const [showPatternsInChat, setShowPatternsInChat] = useState(true);
    // Resize surcharge — USD added per resize action on top of the model
    // cost. Editable from this section, persisted via /credits/cop-config.
    const [resizeCostUsd, setResizeCostUsd] = useState("0.20");
    // Extra reference image cost — USD per additional reference / apoyo
    // beyond the first one. Same persistence path as resizeCostUsd.
    const [extraRefCostUsd, setExtraRefCostUsd] = useState("0.10");
    // Copy Studio (GPT-5.2) — USD per 1M tokens. Defaults match OpenAI's
    // 2026-06 list price. Editable here so the super_admin can react to
    // OpenAI price changes without a redeploy.
    const [copyInputUsdPerM, setCopyInputUsdPerM] = useState("1.25");
    const [copyOutputUsdPerM, setCopyOutputUsdPerM] = useState("10.0");
    const [savingChatDefaults, setSavingChatDefaults] = useState(false);

    const monthOptions = useMemo(() => buildMonthOptions(12), []);
    const confirm = useConfirm();

    const loadConsumption = (month) => {
        api.get(`/credits/consumption?month=${month}`)
            .then((r) => {
                setConsumption(r.data);
                // Hydrate the BACKEND default — useCurrencyPref decides whether
                // to honour it or stick with the user's localStorage choice.
                if (r.data.currency) setBackendCurrency(r.data.currency);
                // Sync the TRM input with the month's resolved TRM (override
                // if set, otherwise the global default). Keeps the input in
                // step with the banner above and lets super admin correct
                // historical months by selecting the month + saving.
                if (typeof r.data.trm_cop_per_usd === "number") {
                    setTrmInput(fmtCopDigits(r.data.trm_cop_per_usd));
                }
            })
            .catch(() => {});
    };

    const load = () => {
        api.get("/admin/stats").then((r) => setStats(r.data));
        api.get("/credits").then((r) => {
            setCredits(r.data);
            const cur = r.data.currency || "USD";
            const trm = Number(r.data.trm_cop_per_usd || 4000);
            const cop = Number(r.data.monthly_budget_cop || 0);
            // The credits-budget input is currency-aware. Use the user's ACTIVE
            // currency (localStorage), not the backend default — otherwise an
            // admin who picked COP locally would see a USD value here.
            const activeCurrency = currency;
            if (activeCurrency === "USD") {
                setBudgetInput(
                    (trm > 0 ? cop / trm : 0).toLocaleString("es-CO", {
                        maximumFractionDigits: 2,
                    }),
                );
            } else {
                setBudgetInput(fmtCopDigits(cop));
            }
            setBackendCurrency(cur);
            setTrmInput(fmtCopDigits(r.data.trm_cop_per_usd || 4000));
            setElitePlanInput(String(r.data.elite_plan_usd ?? 280));
            setTotalBudgetUsdInput(String(r.data.total_budget_usd ?? 800));
            // Chat defaults (per-platform). Falls back to product defaults
            // when the backend hasn't been migrated yet (first deploy).
            setDefaultQuality(r.data.default_quality || "basic");
            setDefaultResolutionId(r.data.default_resolution_id || "square_hd");
            const qs = r.data.quality_surcharge_usd || {};
            setQualitySurcharge({
                basic: String(qs.basic ?? 0),
                medium: String(qs.medium ?? 0.5),
                high: String(qs.high ?? 0.7),
            });
            setShowPatternsInChat(
                typeof r.data.show_patterns_in_chat === "boolean"
                    ? r.data.show_patterns_in_chat
                    : true,
            );
            setResizeCostUsd(String(r.data.resize_cost_usd ?? 0.2));
            setExtraRefCostUsd(String(r.data.extra_ref_cost_usd ?? 0.1));
            setCopyInputUsdPerM(String(r.data.copy_input_usd_per_m ?? 1.25));
            setCopyOutputUsdPerM(String(r.data.copy_output_usd_per_m ?? 10.0));
        });
        api.get("/catalog/resolutions").then((r) => {
            setResolutionsCatalog(r.data || []);
        }).catch(() => {});
        loadConsumption(selectedMonth);
        api.get("/storage").then((r) => {
            setStorage(r.data);
            setStorageInput(String(r.data.limit_gb));
        }).catch(() => {});
        api.get("/storage/breakdown").then((r) => {
            setStorageBreakdown(r.data || []);
        }).catch(() => {});
        // SMTP config — only super admins receive 200 here; admin gets 403
        // and we silently drop it (the section won't render for them anyway).
        if (isSuper) {
            api.get("/admin/smtp")
                .then((r) => {
                    setSmtp(r.data);
                    setSmtpForm({
                        enabled: !!r.data.enabled,
                        server: r.data.server || "",
                        port: r.data.port || 587,
                        user: r.data.user || "",
                        password: "", // never hydrated — keep blank means "no change"
                        from_email: r.data.from_email || "",
                        from_name: r.data.from_name || "",
                        use_tls: r.data.use_tls !== false,
                    });
                })
                .catch(() => {});
            api.get("/admin/recaptcha")
                .then((r) => {
                    setRecaptcha(r.data);
                    setRecaptchaForm({
                        enabled: !!r.data.enabled,
                        site_key: r.data.site_key || "",
                        secret_key: "", // never hydrated
                    });
                })
                .catch(() => {});
        }
    };
    useEffect(load, []);
    // Refresh consumption when month changes (without re-fetching everything).
    useEffect(() => {
        if (credits) loadConsumption(selectedMonth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMonth]);
    // Reformat the credits-budget input when user toggles currency, using the
    // current backend value as source of truth (avoids drift from typing).
    useEffect(() => {
        if (!credits) return;
        const trm = Number(credits.trm_cop_per_usd || 4000);
        const cop = Number(credits.monthly_budget_cop || 0);
        if (currency === "USD") {
            setBudgetInput(
                (trm > 0 ? cop / trm : 0).toLocaleString("es-CO", {
                    maximumFractionDigits: 2,
                }),
            );
        } else {
            setBudgetInput(fmtCopDigits(cop));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency]);

    /**
     * Persist SMTP config. Empty `password` is a sentinel meaning "leave the
     * stored one alone" — the backend handles that (so the admin can edit
     * other fields without being forced to retype the password).
     */
    const saveSmtp = async () => {
        if (!isSuper) return;
        const payload = { ...smtpForm };
        if (!payload.password) delete payload.password;
        if (payload.enabled && (!payload.server || !payload.from_email)) {
            toast.error("Para activar SMTP necesitas servidor y from_email.");
            return;
        }
        setSavingSmtp(true);
        try {
            const r = await api.put("/admin/smtp", payload);
            setSmtp(r.data);
            setSmtpForm((f) => ({ ...f, password: "" }));
            toast.success("Configuración SMTP guardada");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar SMTP");
        } finally {
            setSavingSmtp(false);
        }
    };

    /** Send a one-off test email using the saved SMTP config. */
    const testSmtp = async () => {
        if (!isSuper) return;
        const to = (smtpTestEmail || me?.email || "").trim();
        if (!to) {
            toast.error("Indica un correo de destino.");
            return;
        }
        setTestingSmtp(true);
        try {
            await api.post("/admin/smtp/test", { to_email: to });
            toast.success("Correo de prueba enviado", {
                description: `Revisa la bandeja de ${to}`,
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "El SMTP rechazó el envío");
        } finally {
            setTestingSmtp(false);
        }
    };

    /**
     * Persist reCAPTCHA config. Empty `secret_key` keeps the stored one
     * (matches SMTP password). When enabling, both keys must already be set.
     */
    const saveRecaptcha = async () => {
        if (!isSuper) return;
        const payload = { ...recaptchaForm };
        if (!payload.secret_key) delete payload.secret_key;
        if (
            payload.enabled &&
            (!payload.site_key ||
                (!payload.secret_key && !recaptcha?.has_secret))
        ) {
            toast.error("Para activar reCAPTCHA necesitas site key + secret key.");
            return;
        }
        setSavingRecaptcha(true);
        try {
            const r = await api.put("/admin/recaptcha", payload);
            setRecaptcha(r.data);
            setRecaptchaForm((f) => ({ ...f, secret_key: "" }));
            toast.success("reCAPTCHA actualizado");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar reCAPTCHA");
        } finally {
            setSavingRecaptcha(false);
        }
    };

    /**
     * Wipe the entire library + chats. Hard confirmation: pop a destructive
     * modal first, then send the literal `RESET` keyword the backend
     * requires. There is no undo.
     */
    const resetLibrary = async () => {
        if (!isSuper) return;
        const ok = await confirm({
            title: "¿Borrar TODA la biblioteca y los chats?",
            description:
                "Esto elimina todas las imágenes generadas, los chats con su historial, los logs y reinicia el contador de consumo. NO HAY DESHACER.",
            confirmLabel: "Sí, borrar todo",
            cancelLabel: "Cancelar",
            tone: "destructive",
            kind: "delete",
        });
        if (!ok) return;
        setResettingLibrary(true);
        try {
            const r = await api.post("/admin/reset-library", {
                confirm: "RESET",
                reset_consumed: true,
                wipe_chats: true,
            });
            const parts = [];
            if (r.data.deleted_count) parts.push(`${r.data.deleted_count} imágenes`);
            if (r.data.deleted_chats) parts.push(`${r.data.deleted_chats} chats`);
            if (r.data.deleted_messages) parts.push(`${r.data.deleted_messages} mensajes`);
            toast.success("Limpieza completada", {
                description: parts.length
                    ? parts.join(" · ") + " eliminados."
                    : "Sin registros para borrar.",
            });
            // Refresh consumption + stats so the page reflects the wipe.
            loadConsumption(selectedMonth);
            api.get("/admin/stats").then((rr) => setStats(rr.data));
            window.dispatchEvent(new CustomEvent("kc:credits-updated"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo borrar la biblioteca");
        } finally {
            setResettingLibrary(false);
        }
    };

    const saveStorageLimit = async () => {
        const v = parseFloat(storageInput);
        if (isNaN(v) || v < 0.1) {
            toast.error("Límite inválido (mínimo 0.1 GB)");
            return;
        }
        setSavingStorage(true);
        try {
            const r = await api.put("/storage/limit", { limit_gb: v });
            setStorage(r.data);
            toast.success("Límite de almacenamiento actualizado");
            window.dispatchEvent(new CustomEvent("kc:storage-updated"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally {
            setSavingStorage(false);
        }
    };

    /**
     * Save credits-budget + total-budget. The budget input is currency-aware:
     * when the user picked USD we multiply by TRM to persist as COP. The
     * backend always stores `monthly_budget_cop` (canonical) regardless.
     */
    const saveCopConfig = async () => {
        const trm = Number(credits?.trm_cop_per_usd || 4000);
        // Parse the budget input — it may have either dot-thousand (COP) or
        // comma-decimal (USD) separators. Strip everything that isn't a digit
        // or decimal then convert.
        const raw = String(budgetInput).replace(/\./g, "").replace(",", ".");
        const num = parseFloat(raw);
        const budgetCop =
            currency === "USD" ? Math.round((num || 0) * trm) : num;
        const totalUsd = parseFloat(totalBudgetUsdInput);
        const payload = {};
        if (!isNaN(budgetCop) && budgetCop >= 0) payload.monthly_budget_cop = budgetCop;
        if (isSuper && !isNaN(totalUsd) && totalUsd >= 0) {
            payload.total_budget_usd = totalUsd;
        }
        if (Object.keys(payload).length === 0) {
            toast.error("Nada que actualizar");
            return;
        }
        setSavingCop(true);
        try {
            const r = await api.put("/credits/cop-config", payload);
            setCredits(r.data);
            loadConsumption(selectedMonth);
            toast.success("Presupuesto actualizado");
            window.dispatchEvent(new CustomEvent("kc:credits-updated"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally {
            setSavingCop(false);
        }
    };

    /**
     * Persist super admin preferences:
     *  - trm_cop_per_usd — exchange rate used for USD↔COP conversions.
     *  - elite_plan_usd — monthly platform fee shown above the budget section.
     *
     * Currency is purely a per-user view preference (localStorage), no longer
     * synced to backend — each admin picks USD or COP independently.
     *
     * The TRM entered here applies to the currently-selected month if
     * super admin sets `trm_month` — we persist it as a per-month override so
     * past months keep their historical TRM.
     */
    const saveSuperPrefs = async () => {
        if (!isSuper) return;
        const trmV = parseFloat(parseCopDigits(trmInput));
        const eliteV = parseFloat(elitePlanInput);
        const payload = {};
        if (!isNaN(trmV) && trmV > 0) {
            payload.trm_cop_per_usd = trmV;
            // Also persist a month-specific override so old months aren't
            // retroactively re-valued if the TRM changes later.
            payload.trm_month = selectedMonth;
            payload.trm_month_value = trmV;
        }
        if (!isNaN(eliteV) && eliteV >= 0) payload.elite_plan_usd = eliteV;
        if (Object.keys(payload).length === 0) {
            toast.error("Nada que actualizar");
            return;
        }
        setSavingPrefs(true);
        try {
            const r = await api.put("/credits/cop-config", payload);
            setCredits(r.data);
            loadConsumption(selectedMonth);
            toast.success("Preferencias actualizadas");
            window.dispatchEvent(new CustomEvent("kc:credits-updated"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally {
            setSavingPrefs(false);
        }
    };

    /**
     * Persist chat-wide defaults: quality preset, resolution preset, the
     * per-quality USD surcharges (3 values), and the patterns toggle.
     * Super-admin only — matches the backend authorization on each field.
     */
    const saveChatDefaults = async () => {
        if (!isSuper) return;
        const parseNum = (v) => {
            const n = parseFloat(String(v).replace(",", "."));
            return isNaN(n) || n < 0 ? 0 : n;
        };
        const payload = {
            default_quality: defaultQuality,
            default_resolution_id: defaultResolutionId,
            quality_surcharge_usd: {
                basic: parseNum(qualitySurcharge.basic),
                medium: parseNum(qualitySurcharge.medium),
                high: parseNum(qualitySurcharge.high),
            },
            show_patterns_in_chat: !!showPatternsInChat,
            resize_cost_usd: parseNum(resizeCostUsd),
            extra_ref_cost_usd: parseNum(extraRefCostUsd),
            copy_input_usd_per_m: parseNum(copyInputUsdPerM),
            copy_output_usd_per_m: parseNum(copyOutputUsdPerM),
        };
        setSavingChatDefaults(true);
        try {
            const r = await api.put("/credits/cop-config", payload);
            setCredits(r.data);
            toast.success("Configuración del chat actualizada");
            window.dispatchEvent(new CustomEvent("kc:credits-updated"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally {
            setSavingChatDefaults(false);
        }
    };

    return (
        <div className="space-y-10" data-testid="admin-general-page">
            {credits && consumption && (
                <section data-testid="admin-presupuesto-section">
                    <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
                        <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase flex items-center gap-2">
                            <Wallet className="w-3 h-3" />
                            Presupuesto mensual
                        </p>
                        <div className="flex items-center gap-2">
                            <Calendar className="w-3.5 h-3.5 text-fog-400" />
                            <select
                                data-testid="admin-month-select"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                className="rounded-xl bg-ink-900 border hairline border-ink-600 text-fog-100 text-xs px-3 py-2 outline-none focus:border-periwinkle-500 tabular-nums"
                            >
                                {monthOptions.map((m) => (
                                    <option key={m.value} value={m.value}>
                                        {m.label}
                                    </option>
                                ))}
                            </select>
                            <div
                                className="inline-flex rounded-xl border hairline border-ink-600 bg-ink-900 p-0.5"
                                role="group"
                                data-testid="admin-currency-toggle"
                            >
                                {["USD", "COP"].map((c) => (
                                    <button
                                        key={c}
                                        data-testid={`admin-currency-${c.toLowerCase()}`}
                                        onClick={() => setCurrency(c)}
                                        className={`px-3 py-1 rounded-lg text-[11px] font-mono tracking-widest uppercase transition-colors ${
                                            currency === c
                                                ? "bg-periwinkle-500 text-white"
                                                : "text-fog-400 hover:text-fog-100"
                                        }`}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Elite plan + TRM banner */}
                    <div
                        className="mb-3 rounded-2xl border hairline border-ink-600 bg-ink-900 px-5 py-3 flex items-center gap-4 flex-wrap"
                        data-testid="admin-elite-plan-banner"
                    >
                        <div className="flex items-center gap-2">
                            <Award className="w-4 h-4 text-periwinkle-500" />
                            <span className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                                Plan Elite · tarifa mensual
                            </span>
                            <HelpHint text="Pago fijo mensual del cliente por usar la plataforma. Se cobra aparte de los créditos para imágenes." />
                        </div>
                        <span
                            className="font-display text-xl text-periwinkle-500"
                            data-testid="admin-elite-plan-value"
                        >
                            {fmtUsd(consumption.elite_plan_usd || 280)}
                        </span>
                        {currency === "COP" && (
                            <span className="font-mono text-[11px] text-fog-400">
                                · {fmtCop((consumption.elite_plan_usd || 280) * (consumption.trm_cop_per_usd || 4000))}
                            </span>
                        )}
                        <span className="flex-1" />
                        <span className="font-mono text-[10px] text-fog-400 uppercase tracking-widest flex items-center">
                            TRM <span className="text-fog-100 tabular-nums ml-1">
                                {new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(consumption.trm_cop_per_usd || 0)}
                            </span>{" "}<span className="ml-1">COP / USD</span>
                            <HelpHint text="Tasa de cambio que se aplica para mostrar los valores en otra moneda durante el mes seleccionado. Cada mes puede tener su propia tasa." />
                        </span>
                    </div>

                    <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-6 space-y-6">
                        <div className="grid md:grid-cols-4 gap-6">
                            <CreditStat
                                label="Costo promedio / imagen"
                                value={fmtCurrency(
                                    consumption.avg_cost_cop || 0,
                                    currency,
                                    consumption.trm_cop_per_usd || 1,
                                )}
                                icon={<TrendingUp className="w-4 h-4" />}
                                help={
                                    <HelpHint text="Promedio del valor por imagen durante el mes seleccionado." />
                                }
                            />
                            <CreditStat
                                label="Presupuesto mes"
                                value={fmtCurrency(
                                    consumption.monthly_budget_cop || 0,
                                    currency,
                                    consumption.trm_cop_per_usd || 1,
                                )}
                                help={
                                    <HelpHint text="Tope máximo consumible en imágenes durante el mes. No incluye el fee del Plan Elite." />
                                }
                            />
                            <CreditStat
                                label="Consumido mes"
                                value={fmtCurrency(
                                    consumption.cop_month || 0,
                                    currency,
                                    consumption.trm_cop_per_usd || 1,
                                )}
                                tone="warn"
                                help={
                                    <HelpHint text="Cuánto se ha gastado en imágenes generadas durante el mes seleccionado." />
                                }
                            />
                            <CreditStat
                                label="Restante mes"
                                value={fmtCurrency(
                                    consumption.cop_remaining || 0,
                                    currency,
                                    consumption.trm_cop_per_usd || 1,
                                )}
                                tone={consumption.over_budget ? "danger" : "ok"}
                                help={
                                    <HelpHint text="Saldo restante de créditos para generar nuevas imágenes este mes. Cuando llega a 0, la plataforma bloquea las generaciones." />
                                }
                            />
                        </div>
                        <div>
                            <div className="h-2 rounded-full bg-ink-700 overflow-hidden mb-1.5">
                                <div
                                    className="h-full transition-all duration-500"
                                    style={{
                                        width: `${Math.min(consumption.percent_used_month, 100)}%`,
                                        background: consumption.over_budget
                                            ? "#ff6b8b"
                                            : consumption.percent_used_month < 60
                                              ? "#a5a6ff"
                                              : consumption.percent_used_month < 85
                                                ? "#ffbe7b"
                                                : "#ff6b8b",
                                    }}
                                />
                            </div>
                            <p className="font-mono text-[10px] text-fog-400">
                                {consumption.images_month} imágenes este mes ·{" "}
                                {consumption.percent_used_month.toFixed(1)}% usado
                                {consumption.over_budget && (
                                    <span className="ml-2 text-destructive uppercase tracking-widest">
                                        · límite alcanzado — recargar
                                    </span>
                                )}
                            </p>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            {isSuper && (
                                <div>
                                    <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2 flex items-center">
                                        Presupuesto total del cliente (USD)
                                        <HelpHint text="Sumatoria del fee del Plan Elite + el saldo asignado a créditos para imágenes. Es lo que el cliente paga en total cada mes." />
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-fog-400 font-mono text-sm pointer-events-none select-none">
                                            $
                                        </span>
                                        <input
                                            data-testid="admin-total-budget-input"
                                            type="number"
                                            step="1"
                                            min="0"
                                            value={totalBudgetUsdInput}
                                            onChange={(e) =>
                                                setTotalBudgetUsdInput(e.target.value)
                                            }
                                            placeholder="800"
                                            className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 pl-8 pr-16 py-2.5 outline-none focus:border-periwinkle-500 text-sm tabular-nums"
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-fog-500 font-mono text-[10px] tracking-widest uppercase pointer-events-none select-none">
                                            USD
                                        </span>
                                    </div>
                                    {(() => {
                                        const total = parseFloat(totalBudgetUsdInput) || 0;
                                        const elite = parseFloat(elitePlanInput) || 0;
                                        const credits = Math.max(total - elite, 0);
                                        return (
                                            <p className="text-[10px] font-mono text-fog-500 mt-1 leading-relaxed">
                                                ≈ {fmtUsd(elite)} fee del plan ·{" "}
                                                <span className="text-periwinkle-500">
                                                    {fmtUsd(credits)} para créditos de imágenes
                                                </span>
                                            </p>
                                        );
                                    })()}
                                </div>
                            )}
                            <div>
                                <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2 flex items-center">
                                    Presupuesto del mes créditos ({currency})
                                    <HelpHint text="Tope máximo de gasto permitido en imágenes durante el mes. No incluye el fee del Plan Elite." />
                                </label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-fog-400 font-mono text-sm pointer-events-none select-none">
                                        $
                                    </span>
                                    <input
                                        data-testid="admin-budget-cop-input"
                                        type="text"
                                        inputMode="numeric"
                                        value={budgetInput}
                                        onChange={(e) => {
                                            // Allow plain digits with a single comma
                                            // (USD decimals) or dots (COP thousands).
                                            const val = e.target.value;
                                            if (currency === "COP") {
                                                setBudgetInput(
                                                    fmtCopDigits(parseCopDigits(val)),
                                                );
                                            } else {
                                                // Sanitize to digits + at most one comma
                                                const cleaned = val
                                                    .replace(/[^\d,]/g, "")
                                                    .replace(/(,.*),/g, "$1");
                                                setBudgetInput(cleaned);
                                            }
                                        }}
                                        placeholder={currency === "USD" ? "Ej: 520" : "Ej: 2.080.000"}
                                        className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 pl-8 pr-16 py-2.5 outline-none focus:border-periwinkle-500 text-sm tabular-nums"
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-fog-500 font-mono text-[10px] tracking-widest uppercase pointer-events-none select-none">
                                        {currency}
                                    </span>
                                </div>
                                <p className="text-[10px] font-mono text-fog-500 mt-1">
                                    Máximo consumible en imágenes (excluye el fee del plan).
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-3 flex-wrap">
                            <button
                                data-testid="admin-save-cop-btn"
                                onClick={saveCopConfig}
                                disabled={savingCop}
                                className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                            >
                                <Save className="w-4 h-4" />
                                {savingCop ? "Guardando…" : "Guardar presupuesto"}
                            </button>
                        </div>

                        {/* Super admin preferences: currency persistence, TRM (per month), Elite plan fee */}
                        {isSuper && (
                            <div
                                className="border-t hairline border-ink-600 pt-5 space-y-4"
                                data-testid="admin-super-prefs"
                            >
                                <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                                    Preferencias super admin
                                </p>
                                <div className="grid md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2 flex items-center">
                                            TRM del mes ({selectedMonth})
                                            <HelpHint text="Tasa de cambio para convertir valores entre las dos monedas del mes seleccionado. Cada mes conserva su propia tasa histórica. Guarda 0 para borrar la tasa específica del mes y usar la global." />
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-fog-400 font-mono text-sm pointer-events-none select-none">
                                                $
                                            </span>
                                            <input
                                                data-testid="admin-trm-input"
                                                type="text"
                                                inputMode="numeric"
                                                value={trmInput}
                                                onChange={(e) =>
                                                    setTrmInput(
                                                        fmtCopDigits(parseCopDigits(e.target.value)),
                                                    )
                                                }
                                                placeholder="Ej: 4.000"
                                                className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 pl-8 pr-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm tabular-nums"
                                            />
                                        </div>
                                        <p className="text-[9px] font-mono text-fog-500 mt-1">
                                            COP por 1 USD. Override para el mes seleccionado.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2 flex items-center">
                                            Plan Elite — tarifa mensual (USD)
                                            <HelpHint text="Pago fijo mensual por usar la plataforma. Se cobra aparte de los créditos para imágenes." />
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-fog-400 font-mono text-sm pointer-events-none select-none">
                                                $
                                            </span>
                                            <input
                                                data-testid="admin-elite-input"
                                                type="number"
                                                step="1"
                                                min="0"
                                                value={elitePlanInput}
                                                onChange={(e) => setElitePlanInput(e.target.value)}
                                                placeholder="280"
                                                className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 pl-8 pr-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm tabular-nums"
                                            />
                                        </div>
                                        <p className="text-[9px] font-mono text-fog-500 mt-1">
                                            Pago fijo por el plan de la plataforma.
                                        </p>
                                    </div>
                                    <div className="flex items-end">
                                        <button
                                            data-testid="admin-save-prefs-btn"
                                            onClick={saveSuperPrefs}
                                            disabled={savingPrefs}
                                            className="w-full rounded-xl border hairline border-periwinkle-500 text-periwinkle-500 hover:bg-periwinkle-500 hover:text-ink-950 px-5 py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                                        >
                                            <Save className="w-4 h-4" />
                                            {savingPrefs ? "Guardando…" : "Guardar preferencias"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Configuración del Chat — quality presets, default resolution,
              * patterns toggle. Super admin only. */}
            {isSuper && credits && (
                <section data-testid="admin-chat-defaults-section">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-4 flex items-center gap-2">
                        <ImageIcon className="w-3 h-3" />
                        Chat · valores por defecto + calidad de imagen
                        <HelpHint text="Define la calidad y resolución por defecto del chat para todos los usuarios, los costos extra por usar calidades altas, y si los patrones de construcción aparecen en el chat." />
                    </p>
                    <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-6 space-y-6">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase block mb-2">
                                    Calidad por defecto
                                    <HelpHint text="Calidad seleccionada al abrir un chat nuevo. El usuario puede cambiarla antes de generar." />
                                </label>
                                <select
                                    data-testid="admin-default-quality"
                                    value={defaultQuality}
                                    onChange={(e) => setDefaultQuality(e.target.value)}
                                    className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                                >
                                    <option value="basic">Básico · resolución base · ×1.0</option>
                                    <option value="medium">Moderado · ×1.5 px por lado</option>
                                    <option value="high">Súper calidad · ×2.0 px por lado</option>
                                </select>
                            </div>
                            <div>
                                <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase block mb-2">
                                    Resolución por defecto
                                    <HelpHint text="Preset de aspecto/tamaño base usado al abrir un chat nuevo. La calidad aplica un multiplicador sobre esta base." />
                                </label>
                                <select
                                    data-testid="admin-default-resolution"
                                    value={defaultResolutionId}
                                    onChange={(e) =>
                                        setDefaultResolutionId(e.target.value)
                                    }
                                    className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                                >
                                    {(resolutionsCatalog.length
                                        ? resolutionsCatalog
                                        : [{ id: "square_hd", label: "1:1 · 1024×1024" }]
                                    ).map((opt) => (
                                        <option key={opt.id} value={opt.id}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2 flex items-center">
                                Costos adicionales por calidad (USD por imagen)
                                <HelpHint text="Se suman al costo del modelo (cost_per_image_cop) cuando el usuario elige esa calidad. El valor en USD se convierte automáticamente a COP usando la TRM del mes." />
                            </p>
                            <div className="grid md:grid-cols-3 gap-3">
                                {[
                                    { key: "basic", label: "Básico" },
                                    { key: "medium", label: "Moderado" },
                                    { key: "high", label: "Súper calidad" },
                                ].map((tier) => (
                                    <div key={tier.key}>
                                        <label className="font-mono text-[9px] tracking-widest text-fog-500 uppercase block mb-1">
                                            {tier.label}
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fog-400 font-mono text-sm pointer-events-none select-none">
                                                $
                                            </span>
                                            <input
                                                data-testid={`admin-quality-surcharge-${tier.key}`}
                                                type="text"
                                                inputMode="decimal"
                                                value={qualitySurcharge[tier.key]}
                                                onChange={(e) =>
                                                    setQualitySurcharge((q) => ({
                                                        ...q,
                                                        [tier.key]: e.target.value,
                                                    }))
                                                }
                                                placeholder="0"
                                                className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 pl-7 pr-12 py-2 outline-none focus:border-periwinkle-500 text-sm tabular-nums"
                                            />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-fog-500 font-mono text-[10px] tracking-widest uppercase pointer-events-none">
                                                USD
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="font-mono text-[9px] text-fog-500 mt-2 leading-relaxed">
                                Ejemplo: si el modelo cuesta 0.78 USD y el usuario elige Súper calidad con sobrecargo 0.70 USD, la imagen se carga al presupuesto como 1.48 USD.
                            </p>
                        </div>
                        <div className="pt-3 border-t hairline border-ink-700">
                            <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2 flex items-center">
                                Costo de redimensionar (USD por acción)
                                <HelpHint text="Se suma al costo del modelo cada vez que un usuario ejecuta Redimensionar (sea desde el chat o desde la biblioteca). Esta acción también consume cuota mensual del usuario y del presupuesto de la plataforma." />
                            </p>
                            <div className="max-w-xs">
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fog-400 font-mono text-sm pointer-events-none select-none">
                                        $
                                    </span>
                                    <input
                                        data-testid="admin-resize-cost-usd"
                                        type="text"
                                        inputMode="decimal"
                                        value={resizeCostUsd}
                                        onChange={(e) =>
                                            setResizeCostUsd(e.target.value)
                                        }
                                        placeholder="0.20"
                                        className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 pl-7 pr-12 py-2 outline-none focus:border-periwinkle-500 text-sm tabular-nums"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-fog-500 font-mono text-[10px] tracking-widest uppercase pointer-events-none">
                                        USD
                                    </span>
                                </div>
                                <p className="font-mono text-[9px] text-fog-500 mt-2 leading-relaxed">
                                    Por defecto 0.20 USD. Convertido en COP automáticamente usando la TRM del mes.
                                </p>
                            </div>
                        </div>
                        <div className="pt-3 border-t hairline border-ink-700">
                            <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2 flex items-center">
                                Costo por imagen de apoyo/referencia extra (USD)
                                <HelpHint text="La primera referencia y el primer apoyo son gratis. Cada imagen adicional (de cualquier tipo) suma este valor al costo de la generación. Ejemplo: 2 referencias + 2 apoyos = 2 extras = +0.20 USD si está en 0.10." />
                            </p>
                            <div className="max-w-xs">
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fog-400 font-mono text-sm pointer-events-none select-none">
                                        $
                                    </span>
                                    <input
                                        data-testid="admin-extra-ref-cost-usd"
                                        type="text"
                                        inputMode="decimal"
                                        value={extraRefCostUsd}
                                        onChange={(e) =>
                                            setExtraRefCostUsd(e.target.value)
                                        }
                                        placeholder="0.10"
                                        className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 pl-7 pr-12 py-2 outline-none focus:border-periwinkle-500 text-sm tabular-nums"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-fog-500 font-mono text-[10px] tracking-widest uppercase pointer-events-none">
                                        USD
                                    </span>
                                </div>
                                <p className="font-mono text-[9px] text-fog-500 mt-2 leading-relaxed">
                                    Por defecto 0.10 USD por cada imagen extra. Aplica a referencias del portafolio y a apoyos subidos directamente.
                                </p>
                            </div>
                        </div>
                        <div className="pt-3 border-t hairline border-ink-700">
                            <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2 flex items-center">
                                Costo Copy Studio · GPT-5.2 (USD por 1M tokens)
                                <HelpHint text="Precio que OpenAI cobra por cada millón de tokens del modelo GPT-5.2. El input son los tokens que envías (tu idea + voz de marca + ejemplos); el output son los tokens del copy generado. Cada interacción del chat de copy usa estos precios para calcular su costo en USD/COP." />
                            </p>
                            <div className="grid grid-cols-2 gap-3 max-w-md">
                                <div>
                                    <label className="font-mono text-[10px] text-fog-500 uppercase tracking-widest block mb-1">
                                        Input
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fog-400 font-mono text-sm pointer-events-none">$</span>
                                        <input
                                            data-testid="admin-copy-input-usd-per-m"
                                            type="text"
                                            inputMode="decimal"
                                            value={copyInputUsdPerM}
                                            onChange={(e) => setCopyInputUsdPerM(e.target.value)}
                                            placeholder="1.25"
                                            className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 pl-7 pr-14 py-2 outline-none focus:border-periwinkle-500 text-sm tabular-nums"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-fog-500 font-mono text-[9px] tracking-widest uppercase pointer-events-none">/1M</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="font-mono text-[10px] text-fog-500 uppercase tracking-widest block mb-1">
                                        Output
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fog-400 font-mono text-sm pointer-events-none">$</span>
                                        <input
                                            data-testid="admin-copy-output-usd-per-m"
                                            type="text"
                                            inputMode="decimal"
                                            value={copyOutputUsdPerM}
                                            onChange={(e) => setCopyOutputUsdPerM(e.target.value)}
                                            placeholder="10.00"
                                            className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 pl-7 pr-14 py-2 outline-none focus:border-periwinkle-500 text-sm tabular-nums"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-fog-500 font-mono text-[9px] tracking-widest uppercase pointer-events-none">/1M</span>
                                    </div>
                                </div>
                            </div>
                            <p className="font-mono text-[9px] text-fog-500 mt-2 leading-relaxed">
                                Precios actuales OpenAI gpt-5.2 (jun 2026): $1.25 in / $10.00 out por 1M tokens. Un copy típico cuesta ~$0.005 USD ≈ 20 COP.
                            </p>
                        </div>
                        <div className="flex items-center justify-between gap-4 pt-2 border-t hairline border-ink-700">
                            <div>
                                <p className="text-fog-100 text-sm">
                                    Patrones de construcción en el chat
                                </p>
                                <p className="font-mono text-[10px] text-fog-500 mt-0.5">
                                    {showPatternsInChat
                                        ? "Visibles — los usuarios pueden activar patrones (Z, F, Gutenberg, etc.) en la barra de configuración."
                                        : "Ocultos — la barra de configuración del chat no muestra patrones."}
                                </p>
                            </div>
                            <label
                                className="inline-flex items-center gap-3 cursor-pointer shrink-0"
                                data-testid="admin-patterns-toggle"
                            >
                                <span className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                                    {showPatternsInChat ? "Visible" : "Oculto"}
                                </span>
                                <span className="relative inline-block w-10 h-5">
                                    <input
                                        type="checkbox"
                                        checked={showPatternsInChat}
                                        onChange={(e) =>
                                            setShowPatternsInChat(e.target.checked)
                                        }
                                        className="sr-only peer"
                                    />
                                    <span className="block w-10 h-5 rounded-full bg-ink-700 peer-checked:bg-periwinkle-500 transition-colors" />
                                    <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                                </span>
                            </label>
                        </div>
                        <div className="flex justify-end">
                            <button
                                data-testid="admin-save-chat-defaults-btn"
                                onClick={saveChatDefaults}
                                disabled={savingChatDefaults}
                                className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                            >
                                <Save className="w-4 h-4" />
                                {savingChatDefaults ? "Guardando…" : "Guardar configuración del chat"}
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {/* Servidor SMTP — super admin only. Hidden when not super_admin. */}
            {isSuper && smtp && (
                <section data-testid="admin-smtp-section">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-4 flex items-center gap-2">
                        <Mail className="w-3 h-3" />
                        Servidor SMTP — entrega de códigos por correo
                        <HelpHint text="Cuando está activo, los códigos del login se envían por correo. Si el correo no está registrado, el sistema responde igualmente 'código enviado' pero no envía nada (anti-enumeración)." />
                    </p>
                    <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-6 space-y-5">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <p className="text-fog-100 text-sm">
                                    {smtpForm.enabled
                                        ? "Activo — los logins envían correo real"
                                        : "Inactivo — el código se muestra en la pantalla de login"}
                                </p>
                                <p className="font-mono text-[10px] text-fog-500 mt-1">
                                    Estado guardado: {smtp.enabled ? "ON" : "OFF"} ·{" "}
                                    {smtp.has_password ? "password guardado" : "sin password"}
                                </p>
                            </div>
                            <label
                                className="inline-flex items-center gap-3 cursor-pointer"
                                data-testid="admin-smtp-enabled-toggle"
                            >
                                <span className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                                    {smtpForm.enabled ? "Activo" : "Inactivo"}
                                </span>
                                <span className="relative inline-block w-10 h-5">
                                    <input
                                        type="checkbox"
                                        checked={smtpForm.enabled}
                                        onChange={(e) =>
                                            setSmtpForm((f) => ({
                                                ...f,
                                                enabled: e.target.checked,
                                            }))
                                        }
                                        className="sr-only peer"
                                    />
                                    <span className="block w-10 h-5 rounded-full bg-ink-700 peer-checked:bg-periwinkle-500 transition-colors" />
                                    <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                                </span>
                            </label>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase block mb-2">
                                    Servidor SMTP
                                </label>
                                <input
                                    data-testid="admin-smtp-server"
                                    type="text"
                                    value={smtpForm.server}
                                    onChange={(e) =>
                                        setSmtpForm((f) => ({ ...f, server: e.target.value }))
                                    }
                                    placeholder="smtp.gmail.com"
                                    className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase block mb-2">
                                    Puerto
                                </label>
                                <input
                                    data-testid="admin-smtp-port"
                                    type="number"
                                    min="1"
                                    max="65535"
                                    value={smtpForm.port}
                                    onChange={(e) =>
                                        setSmtpForm((f) => ({
                                            ...f,
                                            port: Number(e.target.value) || 587,
                                        }))
                                    }
                                    placeholder="587"
                                    className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm tabular-nums"
                                />
                                <p className="font-mono text-[9px] text-fog-500 mt-1">
                                    587 = STARTTLS · 465 = SSL implícito
                                </p>
                            </div>
                            <div>
                                <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase block mb-2">
                                    Usuario
                                </label>
                                <input
                                    data-testid="admin-smtp-user"
                                    type="text"
                                    autoComplete="off"
                                    value={smtpForm.user}
                                    onChange={(e) =>
                                        setSmtpForm((f) => ({ ...f, user: e.target.value }))
                                    }
                                    placeholder="user@empresa.com"
                                    className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase block mb-2">
                                    Contraseña
                                    <HelpHint text="Si dejas el campo vacío al guardar, la contraseña actual se conserva. Para Gmail/Workspace genera un App Password (la cuenta debe tener 2FA activado)." />
                                </label>
                                <div className="relative">
                                    <input
                                        data-testid="admin-smtp-password"
                                        type={showSmtpPwd ? "text" : "password"}
                                        autoComplete="new-password"
                                        value={smtpForm.password}
                                        onChange={(e) =>
                                            setSmtpForm((f) => ({
                                                ...f,
                                                password: e.target.value,
                                            }))
                                        }
                                        placeholder={
                                            smtp.has_password
                                                ? "•••••••••• (guardado)"
                                                : "App password / contraseña"
                                        }
                                        className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 pr-10 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm font-mono"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowSmtpPwd((v) => !v)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-fog-400 hover:text-fog-100"
                                        tabIndex={-1}
                                    >
                                        {showSmtpPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase block mb-2">
                                    From email (remitente)
                                </label>
                                <input
                                    data-testid="admin-smtp-from-email"
                                    type="email"
                                    value={smtpForm.from_email}
                                    onChange={(e) =>
                                        setSmtpForm((f) => ({ ...f, from_email: e.target.value }))
                                    }
                                    placeholder="no-reply@empresa.com"
                                    className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase block mb-2">
                                    From name (opcional)
                                </label>
                                <input
                                    data-testid="admin-smtp-from-name"
                                    type="text"
                                    value={smtpForm.from_name}
                                    onChange={(e) =>
                                        setSmtpForm((f) => ({ ...f, from_name: e.target.value }))
                                    }
                                    placeholder="KreaClone"
                                    className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                                />
                            </div>
                        </div>
                        <label
                            className="inline-flex items-center gap-2 cursor-pointer"
                            data-testid="admin-smtp-tls-toggle"
                        >
                            <input
                                type="checkbox"
                                checked={smtpForm.use_tls}
                                onChange={(e) =>
                                    setSmtpForm((f) => ({ ...f, use_tls: e.target.checked }))
                                }
                                className="sr-only peer"
                            />
                            <span className="relative inline-block w-9 h-5">
                                <span className="block w-9 h-5 rounded-full bg-ink-700 peer-checked:bg-periwinkle-500 transition-colors" />
                                <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
                            </span>
                            <span className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                                Usar STARTTLS (recomendado para puerto 587)
                            </span>
                        </label>
                        <div className="flex flex-wrap gap-3 items-end pt-3 border-t hairline border-ink-700">
                            <div className="flex-1 min-w-[260px]">
                                <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase block mb-2">
                                    Correo de prueba — enviar a
                                </label>
                                <input
                                    data-testid="admin-smtp-test-email"
                                    type="email"
                                    value={smtpTestEmail}
                                    onChange={(e) => setSmtpTestEmail(e.target.value)}
                                    placeholder={me?.email || "tu@empresa.com"}
                                    className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                                />
                            </div>
                            <button
                                data-testid="admin-smtp-test-btn"
                                onClick={testSmtp}
                                disabled={testingSmtp}
                                className="rounded-xl border hairline border-periwinkle-500 text-periwinkle-500 hover:bg-periwinkle-500 hover:text-ink-950 px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
                            >
                                <Send className="w-4 h-4" />
                                {testingSmtp ? "Enviando…" : "Enviar correo de prueba"}
                            </button>
                            <button
                                data-testid="admin-smtp-save-btn"
                                onClick={saveSmtp}
                                disabled={savingSmtp}
                                className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                            >
                                <Save className="w-4 h-4" />
                                {savingSmtp ? "Guardando…" : "Guardar SMTP"}
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {/* reCAPTCHA — super admin only. v2 checkbox. */}
            {isSuper && recaptcha && (
                <section data-testid="admin-recaptcha-section">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-4 flex items-center gap-2">
                        <Shield className="w-3 h-3" />
                        reCAPTCHA — protección anti-bots en login
                        <HelpHint text="Cuando está activo, el formulario de login muestra el cuadrito 'No soy un robot'. Bots automáticos no pasan. Las llaves se obtienen en google.com/recaptcha/admin." />
                    </p>
                    <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-6 space-y-5">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <p className="text-fog-100 text-sm">
                                    {recaptchaForm.enabled
                                        ? "Activo — el login exige verificación reCAPTCHA"
                                        : "Inactivo — el login no muestra reCAPTCHA"}
                                </p>
                                <p className="font-mono text-[10px] text-fog-500 mt-1">
                                    Estado guardado: {recaptcha.enabled ? "ON" : "OFF"} ·{" "}
                                    {recaptcha.has_secret ? "secret guardado" : "sin secret"} ·{" "}
                                    v2 checkbox
                                </p>
                            </div>
                            <label
                                className="inline-flex items-center gap-3 cursor-pointer"
                                data-testid="admin-recaptcha-enabled-toggle"
                            >
                                <span className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                                    {recaptchaForm.enabled ? "Activo" : "Inactivo"}
                                </span>
                                <span className="relative inline-block w-10 h-5">
                                    <input
                                        type="checkbox"
                                        checked={recaptchaForm.enabled}
                                        onChange={(e) =>
                                            setRecaptchaForm((f) => ({
                                                ...f,
                                                enabled: e.target.checked,
                                            }))
                                        }
                                        className="sr-only peer"
                                    />
                                    <span className="block w-10 h-5 rounded-full bg-ink-700 peer-checked:bg-periwinkle-500 transition-colors" />
                                    <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                                </span>
                            </label>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase block mb-2">
                                    Site key (pública)
                                </label>
                                <input
                                    data-testid="admin-recaptcha-site-key"
                                    type="text"
                                    autoComplete="off"
                                    value={recaptchaForm.site_key}
                                    onChange={(e) =>
                                        setRecaptchaForm((f) => ({
                                            ...f,
                                            site_key: e.target.value,
                                        }))
                                    }
                                    placeholder="6Lf..."
                                    className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm font-mono"
                                />
                                <p className="font-mono text-[9px] text-fog-500 mt-1">
                                    Embebida en la página de login.
                                </p>
                            </div>
                            <div>
                                <label className="font-mono text-[10px] tracking-widest text-fog-400 uppercase block mb-2">
                                    Secret key (privada)
                                    <HelpHint text="Solo el backend la usa para validar contra Google. Si la dejas vacía al guardar, se conserva la actual." />
                                </label>
                                <div className="relative">
                                    <input
                                        data-testid="admin-recaptcha-secret-key"
                                        type={showRecaptchaSecret ? "text" : "password"}
                                        autoComplete="new-password"
                                        value={recaptchaForm.secret_key}
                                        onChange={(e) =>
                                            setRecaptchaForm((f) => ({
                                                ...f,
                                                secret_key: e.target.value,
                                            }))
                                        }
                                        placeholder={
                                            recaptcha.has_secret
                                                ? "•••••••••• (guardada)"
                                                : "6Lf..."
                                        }
                                        className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 pr-10 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm font-mono"
                                    />
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowRecaptchaSecret((v) => !v)
                                        }
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-fog-400 hover:text-fog-100"
                                        tabIndex={-1}
                                    >
                                        {showRecaptchaSecret ? (
                                            <EyeOff className="w-4 h-4" />
                                        ) : (
                                            <Eye className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end pt-3 border-t hairline border-ink-700">
                            <button
                                data-testid="admin-recaptcha-save-btn"
                                onClick={saveRecaptcha}
                                disabled={savingRecaptcha}
                                className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                            >
                                <Save className="w-4 h-4" />
                                {savingRecaptcha ? "Guardando…" : "Guardar reCAPTCHA"}
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {storage && (
                <section data-testid="admin-storage-section">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-4 flex items-center gap-2">
                        <HardDrive className="w-3 h-3" />
                        Almacenamiento (límite plataforma)
                    </p>
                    <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-6 grid md:grid-cols-3 gap-6">
                        <CreditStat label="Límite" value={`${storage.limit_gb.toFixed(1)} GB`} icon={<HardDrive className="w-4 h-4" />} />
                        <CreditStat label="Usado" value={`${storage.used_gb.toFixed(2)} GB`} tone="warn" />
                        <CreditStat label="Disponible" value={`${storage.remaining_gb.toFixed(2)} GB`} tone="ok" />
                        <div className="md:col-span-3">
                            <div className="h-2 rounded-full bg-ink-700 overflow-hidden mb-3">
                                <div
                                    className="h-full transition-all duration-500"
                                    style={{
                                        width: `${Math.min(storage.percent_used, 100)}%`,
                                        background:
                                            storage.percent_used < 60
                                                ? "#8fe7d0"
                                                : storage.percent_used < 85
                                                  ? "#ffbe7b"
                                                  : "#ff6b8b",
                                    }}
                                />
                            </div>
                            <p className="font-mono text-[10px] text-fog-400 mb-3">
                                {storage.files_count} archivo{storage.files_count === 1 ? "" : "s"} · {storage.percent_used.toFixed(1)}% usado
                            </p>
                            {isSuper ? (
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <input
                                        data-testid="admin-storage-limit-input"
                                        type="number"
                                        step="0.1"
                                        min="0.1"
                                        value={storageInput}
                                        onChange={(e) => setStorageInput(e.target.value)}
                                        placeholder="Límite en GB"
                                        className="flex-1 rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                                    />
                                    <button
                                        data-testid="admin-save-storage-btn"
                                        onClick={saveStorageLimit}
                                        disabled={savingStorage}
                                        className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 px-5 py-2.5 text-sm font-medium flex items-center gap-2"
                                    >
                                        <Save className="w-4 h-4" />
                                        {savingStorage ? "Guardando…" : "Guardar límite"}
                                    </button>
                                </div>
                            ) : (
                                <p className="text-xs text-fog-500 font-mono">
                                    El plan actual cubre hasta 10 GB de almacenamiento (Plan Elite).
                                </p>
                            )}
                        </div>
                    </div>
                    {storageBreakdown.length > 0 && (
                        <div className="mt-4 rounded-2xl border hairline border-ink-600 bg-ink-900 divide-y divide-ink-600">
                            {storageBreakdown.map((b) => (
                                <div
                                    key={b.brand_id}
                                    data-testid={`admin-storage-row-${b.brand_id}`}
                                    className="px-5 py-3 flex items-center gap-4"
                                >
                                    <span
                                        className="w-2.5 h-2.5 rounded-full shrink-0"
                                        style={{ background: b.color || "#a5a6ff" }}
                                    />
                                    <span className="flex-1 text-sm text-fog-100 truncate">
                                        {b.brand_name}
                                    </span>
                                    <span className="font-mono text-[11px] text-fog-400">
                                        refs {fmtBytes(b.references_bytes)} · lib {fmtBytes(b.library_bytes)} · logos {fmtBytes(b.logos_bytes)}
                                    </span>
                                    <span className="font-display text-lg text-periwinkle-500 min-w-[80px] text-right">
                                        {fmtBytes(b.total_bytes)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {stats && (
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Stat label="Imágenes generadas" value={stats.total_images} />
                    <Stat label="Chats creados" value={stats.total_chats} />
                    <Stat label="Marcas" value={stats.total_brands || 0} />
                    <Stat label="Referencias cargadas" value={stats.total_references} />
                </section>
            )}

            {stats?.per_brand?.length > 0 && (
                <section>
                    <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-4">
                        Uso por marca
                    </p>
                    <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 divide-y divide-ink-600">
                        {stats.per_brand.map((r) => (
                            <div key={r.brand_id} className="px-5 py-3 flex items-center justify-between">
                                <span className="text-sm text-fog-100">{r.brand_name}</span>
                                <span className="font-display text-lg text-periwinkle-500">
                                    {r.count}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {isSuper && <PlatformConfigSection />}

            {/* Danger zone — destructive actions, super admin only. Lives at
               the end of the page so it never sits next to a normal save
               button by accident. */}
            {isSuper && (
                <section data-testid="admin-danger-zone">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-destructive uppercase mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-3 h-3" />
                        Zona peligrosa
                    </p>
                    <div className="rounded-2xl border hairline border-destructive/40 bg-destructive/5 p-6 space-y-4">
                        <div>
                            <p className="text-fog-100 text-sm font-medium">
                                Borrar biblioteca, chats y logs
                            </p>
                            <p className="text-fog-400 text-xs mt-1 leading-relaxed">
                                Elimina <strong>todas</strong> las imágenes
                                generadas, los chats con su historial completo,
                                los logs y reinicia el contador de consumo
                                mensual. Esta acción es{" "}
                                <strong>irreversible</strong> — úsala solo para
                                limpiar datos de prueba antes de salir a producción.
                            </p>
                        </div>
                        <div className="flex justify-end">
                            <button
                                data-testid="admin-reset-library-btn"
                                onClick={resetLibrary}
                                disabled={resettingLibrary}
                                className="rounded-xl border hairline border-destructive text-destructive hover:bg-destructive hover:text-ink-950 px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                                {resettingLibrary ? "Borrando…" : "Borrar biblioteca, chats y logs"}
                            </button>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}

function PlatformConfigSection() {
    const { config, save } = usePlatformConfig();
    const [form, setForm] = useState(config);
    const [saving, setSaving] = useState(false);
    useEffect(() => setForm(config), [config]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const submit = async () => {
        setSaving(true);
        try {
            await save(form);
            toast.success("Configuración general actualizada");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };
    const reset = () => setForm(config);

    return (
        <section data-testid="admin-platform-config-section">
            <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-4 flex items-center gap-2">
                <Palette className="w-3 h-3" />
                Identidad visual (white-label)
            </p>
            <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-6 space-y-6">
                <p className="text-xs text-fog-400 max-w-2xl">
                    Se aplica en toda la plataforma al guardar (botones, badges,
                    focus rings, favicon y fuente tipográfica).
                </p>

                <div>
                    <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2">
                        Colores
                    </p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <ColorField
                            label="Botones primarios"
                            value={form.primary_color}
                            onChange={(v) => set("primary_color", v)}
                            testId="admin-cfg-primary"
                        />
                        <ColorField
                            label="Primarios — hover"
                            value={form.primary_hover_color}
                            onChange={(v) => set("primary_hover_color", v)}
                            testId="admin-cfg-primary-hover"
                        />
                        <ColorField
                            label="Texto en botones primarios"
                            value={form.primary_text_color}
                            onChange={(v) => set("primary_text_color", v)}
                            testId="admin-cfg-primary-text"
                        />
                        <ColorField
                            label="Botones secundarios"
                            value={form.secondary_color}
                            onChange={(v) => set("secondary_color", v)}
                            testId="admin-cfg-secondary"
                        />
                        <ColorField
                            label="Texto en botones secundarios"
                            value={form.secondary_text_color}
                            onChange={(v) => set("secondary_text_color", v)}
                            testId="admin-cfg-secondary-text"
                        />
                    </div>
                </div>

                <div>
                    <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2 flex items-center gap-2">
                        <Type className="w-3 h-3" />
                        Fuente de la plataforma
                    </p>
                    <select
                        data-testid="admin-cfg-font"
                        value={form.font_family}
                        onChange={(e) => set("font_family", e.target.value)}
                        className="w-full md:w-96 rounded-xl border hairline border-ink-600 bg-ink-900 px-3 py-2.5 text-fog-100 outline-none focus:border-periwinkle-500 transition-colors"
                        style={{ fontFamily: form.font_family }}
                    >
                        {FONT_OPTIONS.map((f) => (
                            <option
                                key={f.family}
                                value={f.family}
                                style={{ fontFamily: f.family }}
                            >
                                {f.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2 flex items-center gap-2">
                        <ImageIcon className="w-3 h-3" />
                        Favicon (ícono de pestaña)
                    </p>
                    <div className="flex items-center gap-3">
                        <img
                            src={form.favicon_url || "/krea-logo.png"}
                            alt="favicon preview"
                            className="w-10 h-10 rounded-lg border hairline border-ink-600 bg-white p-1 object-contain"
                        />
                        <input
                            data-testid="admin-cfg-favicon"
                            type="text"
                            value={form.favicon_url || ""}
                            onChange={(e) => set("favicon_url", e.target.value)}
                            placeholder="URL pública, p.ej. /krea-logo.png"
                            className="flex-1 rounded-xl border hairline border-ink-600 bg-ink-900 px-3 py-2.5 text-sm text-fog-100 outline-none focus:border-periwinkle-500 transition-colors"
                        />
                    </div>
                </div>

                <div>
                    <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2 flex items-center gap-2">
                        <ImageIcon className="w-3 h-3" />
                        Logo de la plataforma (sidebar + login)
                    </p>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-3">
                            <img
                                src={form.logo_url || "/krea-logo.png"}
                                alt="logo preview"
                                className="h-10 w-auto max-w-[120px] object-contain rounded-lg border hairline border-ink-600 bg-white p-1"
                            />
                            <input
                                data-testid="admin-cfg-logo"
                                type="text"
                                value={form.logo_url || ""}
                                onChange={(e) => set("logo_url", e.target.value)}
                                placeholder="URL del logo, p.ej. /mi-logo.png"
                                className="flex-1 rounded-xl border hairline border-ink-600 bg-ink-900 px-3 py-2.5 text-sm text-fog-100 outline-none focus:border-periwinkle-500 transition-colors"
                            />
                        </div>
                        <div>
                            <p className="font-mono text-[9px] tracking-widest text-fog-400 uppercase mb-1">
                                Nombre de la plataforma
                            </p>
                            <input
                                data-testid="admin-cfg-platform-name"
                                type="text"
                                value={form.platform_name || ""}
                                onChange={(e) => set("platform_name", e.target.value)}
                                placeholder="Krea.AI"
                                className="w-full rounded-xl border hairline border-ink-600 bg-ink-900 px-3 py-2.5 text-sm text-fog-100 outline-none focus:border-periwinkle-500 transition-colors"
                            />
                        </div>
                    </div>
                </div>

                <div className="border-t hairline border-ink-600 pt-5 flex flex-wrap items-center gap-3">
                    <div
                        className="rounded-full px-5 h-10 inline-flex items-center gap-2 font-medium text-sm"
                        style={{
                            background: form.primary_color,
                            color: form.primary_text_color,
                        }}
                    >
                        Botón primario
                    </div>
                    <div
                        className="rounded-full px-5 h-10 inline-flex items-center gap-2 font-medium text-sm border hairline"
                        style={{
                            background: form.secondary_color,
                            color: form.secondary_text_color,
                            borderColor: "rgba(10,14,42,0.08)",
                        }}
                    >
                        Botón secundario
                    </div>
                    <div className="flex-1" />
                    <button
                        onClick={reset}
                        className="inline-flex items-center gap-2 rounded-xl border hairline border-ink-600 px-4 h-10 text-sm text-fog-300 hover:text-fog-100 hover:border-periwinkle-500"
                        data-testid="admin-cfg-reset"
                    >
                        <RotateCcw className="w-4 h-4" /> Deshacer cambios
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving}
                        data-testid="admin-cfg-save"
                        className="inline-flex items-center gap-2 rounded-xl bg-periwinkle-500 hover:bg-periwinkle-400 text-white font-medium px-5 h-10 shadow-glow-sm disabled:opacity-60"
                    >
                        <Save className="w-4 h-4" />
                        {saving ? "Guardando…" : "Guardar"}
                    </button>
                </div>
            </div>
        </section>
    );
}

